import React, { useEffect, useState } from 'react';
import { ChevronRight, Play, Pause, Settings, Trash2, Phone, Clock, FileText, AlertTriangle, MessageCircle, Layers, Package, Boxes, ExternalLink } from 'lucide-react';
import { fmtDate, fmtDateTime, fmtCurrency, formatNumeroCompraComAno, getOrgaoDisplay, isModoAbertoFechado, getProrrogacao, isLicitacaoOver, isJanelaDisputaFechada, computeIpcomOutcome, getFinalRankingRows } from '../utils/formatters';
import { situacaoDaEtapa } from '../utils/faseDaDisputa';
import { portalLinkUrl } from './licitacoes/procurementUi';
import { TONE_BADGE, TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../utils/statusColors';
import { faixaDoRanking } from '../utils/estadoDoRanking';
import { decisaoDoRobo } from '../utils/ultimaDecisao';

interface Props {
  session: any;
  isExpanded: boolean;
  onToggle: () => void;
  onStart: (e: React.MouseEvent) => void;
  onStop: (e: React.MouseEvent) => void;
  onConfig: (e: React.MouseEvent) => void;
  onDelete: (e: React.MouseEvent) => void;
  onCollect?: (e: React.MouseEvent) => void;
  collecting?: boolean;
  // Renderizado quando expandido (todo o painel detalhado vem aqui)
  expandedContent?: React.ReactNode;
  // Helpers reutilizados do componente pai
  statusBadge: { label: string; color: string };
  phaseInfo: { phase: string | null; convocado: boolean | null; outcome: string | null };
  sessionOutcome: { compraEncerrada: boolean; groupStatuses: any[]; ipcom: any; resultadoPendente?: boolean };
  portalLabel?: { label: string; color: string };
  /**
   * L7 (E1, Heitor 24/10/2026): o estado ÚNICO do cartão, derivado da data e do resultado (utils/disputaLista). Com ele, os
   * selos de resultado (coletado, compra encerrada, vencedor/não venceu, pendente, convocado) não aparecem mais um ao
   * lado do outro: viram este selo. Sem ele, o cartão fica como antes.
   */
  estado?: { rotulo: string; tom: 'live' | 'warning' | 'success' | 'neutral' };
  /** Em que seção o cartão está; a configuração do robô só aparece em Hoje e Próximas (D1). */
  secao?: 'hoje' | 'proximas' | 'finalizadas';
}

const fmtCountdown = (ms: number | null): string => {
  if (ms === null || !isFinite(ms) || ms <= 0) return '—';
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};


/** Caracteriza modalidade + estrutura + modo a partir de rankingData + item */
function caracterizar(session: any) {
  const item = session?.item || {};
  const part = session?.rankingData?.participacao;
  const modalidadeId = Number(part?.modalidade ?? item.modalidadeId ?? 0);
  const modoStr = String(part?.modoDisputa ?? item.modoDisputa ?? '');

  const isPregao = modalidadeId === 5 || /preg/i.test(item.modalidadeNome || '');
  const isDispensa = modalidadeId === 6 || modalidadeId === 8 || /dispens/i.test(item.modalidadeNome || '');
  const modLabel = isPregao ? 'Pregão Eletrônico' : isDispensa ? 'Dispensa Eletrônica' : (item.modalidadeNome || 'Licitação');
  const modShort = isPregao ? 'PE' : isDispensa ? 'DE' : '?';
  const modColor = isPregao ? 'bg-blue-500/15 text-blue-300 border-blue-700'
    : isDispensa ? 'bg-purple-500/15 text-purple-300 border-purple-700'
    : 'bg-slate-500/15 text-slate-300 border-central-borda';
  const borderColor = isPregao ? 'border-l-blue-500' : isDispensa ? 'border-l-purple-500' : 'border-l-slate-500';

  // Estrutura — combina rankingData (autoritativo, chega após robô abrir) com
  // itemLimits[].groupNumber (gravado no cadastro pelo automation.service). Bug V:
  // antes do robô iniciar, rankingData está vazio e o card caía em "N Itens" mesmo
  // pra Dispensa grupo c/ itens; fallback via groupIdentificador resolve.
  const r = session?.rankingData || {};
  const itensGrupoRaw = r.itensGrupo?.['-1'];
  const itensGrupoRows: any[] = Array.isArray(itensGrupoRaw) ? itensGrupoRaw
    : (itensGrupoRaw?.registros || itensGrupoRaw?.itens || itensGrupoRaw?.items || []);
  const det = r.detalhamentoGrupo;
  const isExplicitGroup = det?.tipo === 'G' || (det?.qtdeItensDoGrupo && det.qtdeItensDoGrupo > 0);
  const grupoSize = itensGrupoRows.length || det?.qtdeItensDoGrupo || 0;
  const itens = Array.isArray(session?.itemLimits) ? session.itemLimits : [];
  const groupIds = new Set<string>();
  itens.forEach((i: any) => { if (i.groupIdentificador) groupIds.add(String(i.groupIdentificador)); });
  const hasGroup = groupIds.size > 0;
  const hasUngrouped = itens.some((i: any) => !i.groupIdentificador);

  let estLabel: string, EstIcon: any;
  if (grupoSize > 0 || isExplicitGroup) {
    const sz = grupoSize || det?.qtdeItensDoGrupo || itens.length;
    if (hasUngrouped && hasGroup) { estLabel = `Grupo + Itens (${sz} no grupo)`; EstIcon = Layers; }
    else { estLabel = `Grupo c/ ${sz} itens`; EstIcon = Boxes; }
  } else if (hasGroup && hasUngrouped) { estLabel = 'Grupo + Itens'; EstIcon = Layers; }
  else if (hasGroup) { estLabel = `Grupo c/ ${itens.length} itens`; EstIcon = Boxes; }
  else if (itens.length > 1) { estLabel = `${itens.length} Itens`; EstIcon = Layers; }
  else { estLabel = 'Item único'; EstIcon = Package; }

  // Modo (HAR 27/05/26: ModoDisputaCompraEnum oficial):
  //   A=Aberto, F=Fechado, AF=Aberto/Fechado, FA=Fechado/Aberto, D=Dispensa Comum.
  //   Legados PNCP antigo: '2'=Aberto-Fechado.
  // Detecção AF via helper unificado (formatters.isModoAbertoFechado) — checa
  // metadata + fase ao vivo. Mantém modoNome local pros outros checks abaixo.
  const modoNome = String(item.modoDisputaNome || item.modoDisputa || '');
  const isAbertoFechado = isModoAbertoFechado(item, session?.rankingData);
  const isFechadoAberto = modoStr === 'FA' || /fechado.?aberto/i.test(modoNome);
  const isFechado = modoStr === 'F' && !isAbertoFechado && !isFechadoAberto;
  const isDispensaComum = modoStr === 'D' || /dispensa.?com.?disputa|dispensa.?comum/i.test(modoNome);
  const modoLabel = isDispensaComum ? 'Dispensa Comum'
    : isAbertoFechado ? 'Modo Aberto-Fechado'
    : isFechadoAberto ? 'Modo Fechado-Aberto'
    : isFechado ? 'Modo Fechado'
    : 'Modo Aberto';
  const modoColor = isDispensaComum ? 'bg-slate-500/15 text-slate-300 border-central-borda'
    : isAbertoFechado ? 'bg-amber-500/15 text-amber-300 border-amber-700'
    : isFechadoAberto ? 'bg-orange-500/15 text-orange-300 border-orange-700'
    : isFechado ? 'bg-rose-500/15 text-rose-300 border-rose-700'
    : 'bg-emerald-500/15 text-emerald-300 border-emerald-700';

  return { modLabel, modShort, modColor, borderColor, estLabel, EstIcon, modoLabel, modoColor, isAbertoFechado };
}

export function DisputeSessionCard({
  session, isExpanded, onToggle,
  onStart, onStop, onConfig, onDelete, onCollect, collecting,
  expandedContent, statusBadge, phaseInfo, sessionOutcome, portalLabel, estado, secao,
}: Props) {
  const item = session.item || {};
  const tipo = caracterizar(session);

  // ===== Countdown live (quando ativa) =====
  const fimContagemTs = (() => {
    const itensRows = (() => {
      // grupo: itensGrupo['-1']; dispensa/pregão por item: emDisputa (mesma estrutura)
      const ig = session?.rankingData?.itensGrupo?.['-1'] || session?.rankingData?.emDisputa;
      if (Array.isArray(ig)) return ig;
      return ig?.registros || ig?.itens || ig?.items || [];
    })();
    // Candidatos de fim de contagem: rankingData (só popula no "Coletar
    // resultado") + itemLimits[].fimContagem (fonte VIVA durante a disputa,
    // atualizada a cada lance — é o reset de prorrogação pra 2 min).
    const cand: number[] = [];
    for (const r of itensRows) {
      if (r?.dataHoraFimContagem) cand.push(new Date(r.dataHoraFimContagem).getTime());
    }
    for (const lim of (session?.itemLimits || []) as any[]) {
      if (lim?.fimContagem) cand.push(new Date(lim.fimContagem).getTime());
    }
    const valid = cand.filter((ms) => isFinite(ms));
    if (valid.length > 0) {
      // menor que ainda está no futuro (próximo a encerrar); se todos já
      // passaram, o mais recente (último reset de prorrogação conhecido).
      const future = valid.filter((ms) => ms > Date.now());
      return future.length > 0 ? Math.min(...future) : Math.max(...valid);
    }
    // Prioridade: item.dataSessaoFim (vem do portal /participacao, ATUALIZADO
    // após o robô abrir a disputa — mais confiável que disputeEndTime
    // cadastrado, que pode ter sido o dataEncerramento da proposta quando
    // dataSessaoFim ainda não existia no item).
    if (item?.dataSessaoFim) return new Date(item.dataSessaoFim).getTime();
    return session.disputeEndTime ? new Date(session.disputeEndTime).getTime() : null;
  })();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (session.status !== 'ativo') return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [session.status]);
  const isLive = session.status === 'ativo' && fimContagemTs && fimContagemTs > now;
  const tempoRestanteMs = isLive ? (fimContagemTs as number) - now : null;
  // Janela da disputa já passou — pelo item (isLicitacaoOver: último evento de
  // proposta/sessão no passado) ou pelo fim de contagem conhecido. Independe do
  // status: quando a disputa foi no braço, a sessão nunca sai de 'configurando'.
  // (12/09/2026, Piraí do Sul PE 33/2026: sessão de 11/09 ainda oferecia "Iniciar".)
  const encerrada = isLicitacaoOver(item) || (fimContagemTs != null && fimContagemTs < now);
  // A porta do PLAY é outra pergunta (15/09/2026). `encerrada` segue mandando
  // no badge e no "Coletar resultado" — ali "licitação no passado" é o critério
  // certo, e foi o que consertou Tremembé (s125) e Piraí do Sul (s33/2026).
  // Para INICIAR, o que vale é a janela da disputa: `isLicitacaoOver` tratava
  // `dataSessaoInicio` como evento final e escondia o botão no minuto em que a
  // sessão abria — INCA 08:59 e São Vicente 10:00, os dois no mesmo dia.
  const janelaFechada =
    isJanelaDisputaFechada(item, now) || (fimContagemTs != null && fimContagemTs < now);
  // Chip derivado só no render — NÃO existe status 'nao_disputada' no banco.
  // "Configurando" numa sessão que já passou mente sobre o estado do card.
  const badge = encerrada && session.status === 'configurando'
    ? { label: 'Não disputada', color: TONE_BADGE.neutral }
    : encerrada && ['pausado', 'erro'].includes(session.status)
      ? { label: 'Encerrada', color: TONE_BADGE.neutral }
      : statusBadge;

  // ===== Stats do ranking =====
  // Fonte ÚNICA (computeIpcomOutcome → getFinalRankingRows): lê o ranking
  // canônico em qualquer shape (ComprasNet/BLL/PCP) e deriva a posição por
  // valor quando a IPCOM não foi convocada (classificacao vazia). Antes lia só
  // todasPropostas['-1'] direto → vazio em Pregão por grupo (keys '1'..'10')
  // e sem posição pra não-convocado.
  const outcome = computeIpcomOutcome(session?.rankingData);
  const minhaClassif = outcome.classif;
  const classifDerivada = outcome.derived;
  const fornecedoresCount = getFinalRankingRows(session?.rankingData).length;

  // ===== Quadro Informativo =====
  const qi = session?.rankingData?.quadroInformativo;
  const qtdeEscla = qi?.qtdeEsclarecimentos ?? 0;
  const qtdeAvisos = qi?.qtdeAvisos ?? 0;
  const qtdeImpug = qi?.qtdeImpugnacoes ?? 0;
  const hasQi = qtdeEscla > 0 || qtdeAvisos > 0 || qtdeImpug > 0;

  // ===== Responsável =====
  const responsavel = session?.rankingData?.responsavelOperacao;
  const responsavelNome = responsavel?.nome || responsavel?.nomeResponsavel;

  // ===== Fase atual (Pregão Aberto/Fechado e similares) =====
  // FaseItemEnum (HAR 27/05/26): LA=Aberta, AL=Aleatória, LF=Fechada,
  // AF=Aguardando Fechada, FE=Aguardando Encerramento, E=Encerrada.
  // Aparece como chip extra quando há disputa viva (rankingData populado).
  // Não mostra em sessões finalizadas/configurando: o `fase` do rankingData
  // fica stale (caso Dispensa COMANDO DO EXERCITO 110/2026 — encerrada mas
  // payload velho ainda diz fase=LA).
  //
  // O TEXTO do selo e a pergunta "os lances acabaram?" vêm de `situacaoDaEtapa`,
  // a mesma fonte do stepper do header expandido. Até 01/10/2026 o card tinha o
  // próprio mapa de rótulos: em Aberto ele chamava `LF` de "Fase fechada"
  // enquanto o header já dizia "Etapa de lances encerrada" (IPHAN, 29/09).
  const situacao = situacaoDaEtapa(session, now);
  const faseAtual = (() => {
    if (session.status === 'finalizado' || session.status === 'configurando') return null;
    const cor: Record<string, string> = {
      LA: 'bg-emerald-500/15 text-emerald-300 border-emerald-700/40',
      AL: 'bg-cyan-500/15 text-cyan-300 border-cyan-700/40',
      LF: 'bg-purple-500/15 text-purple-300 border-purple-700/40',
      AF: 'bg-purple-500/10 text-purple-400/80 border-purple-700/30',
      FE: 'bg-slate-500/15 text-slate-300 border-central-borda',
    };
    // Com o carimbo do motor (`lancesEncerradosAt`), o selo diz o fim mesmo sem a fase lida: depois de
    // um deploy a sessão segue `ativo` e o `rankingData` pode estar velho ou vazio, e o card mostrava
    // a fase de antes, ou nada (s166, 20/10/2026).
    if (situacao.lancesEncerrados && situacao.carimbo != null) {
      return {
        label: 'Lances encerrados',
        color: cor.FE,
        title: `O robô registrou o fim da etapa de lances em ${fmtDateTime(situacao.carimbo)}`,
        vivo: false,
      };
    }
    const f = situacao.fase;
    if (!f || f === 'E' || !situacao.rotulo) return null;
    const base = {
      label: situacao.rotulo,
      color: situacao.lancesEncerrados ? cor.FE : (cor[f] || cor.FE),
      title: 'Fase atual da disputa',
      vivo: true,
    };
    const rows: any[] = (() => {
      const igRaw = session?.rankingData?.itensGrupo?.['-1'] || session?.rankingData?.emDisputa;
      return Array.isArray(igRaw) ? igRaw : (igRaw?.registros || igRaw?.itens || []);
    })();
    // Aberta que vai terminar em SORTEIO (situacaoAposContagem='AL'): avisa desde
    // já, porque muda a operação — o fim pode vir a qualquer instante e não no
    // relógio mostrado. O portal rotula "Etapa aberta (aleatório)".
    const aposContagem = rows[0]?.situacaoAposContagem
      ?? (session?.itemLimits || []).find((l: any) => l?.situacaoAposContagem)?.situacaoAposContagem;
    if (f !== 'AL' && aposContagem === 'AL') {
      return { ...base, label: `${base.label} · aleatório` };
    }
    return base;
  })();

  // ===== Datas (Propostas vs Disputa) =====
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
  // Dia de referência da disputa: prefere o fim real do portal (dataHoraFimContagem),
  // senão o disputeStartTime cadastrado (que pode estar errado).
  const refDay = fimContagemTs
    ? new Date(fimContagemTs).toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' })
    : (session.disputeStartTime ? new Date(session.disputeStartTime).toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' }) : null);
  const isToday = refDay === today;

  return (
    <div data-cartao-da-disputa className={`rounded-central bg-central-cartao border border-central-borda border-l-[3px] ${estado ? (estado.tom === 'live' || estado.tom === 'warning' ? 'border-l-amber-500' : estado.tom === 'success' ? 'border-l-emerald-500' : 'border-l-transparent') : tipo.borderColor} overflow-hidden hover:bg-central-elevado transition-colors`}>
      {/* ============ HEAD (clickable) ============ */}
      <div className="p-4 cursor-pointer" onClick={onToggle}>
        {/* Tag bar */}
        <div className="flex flex-wrap items-center gap-1.5 mb-3">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border ${tipo.modColor}`}>
            <span className="font-bold opacity-70 text-[10px]">{tipo.modShort}</span>
            {tipo.modLabel}
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border bg-slate-700/40 border-central-borda text-slate-200">
            <tipo.EstIcon size={12} />
            {tipo.estLabel}
          </span>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border ${tipo.modoColor}`}>
            {tipo.modoLabel}
          </span>
          {faseAtual && (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border ${faseAtual.color}`} title={faseAtual.title} data-selo-da-fase>
              <span className={`w-1.5 h-1.5 rounded-full bg-current ${faseAtual.vivo ? 'animate-pulse' : ''}`} />
              {faseAtual.label}
            </span>
          )}
          {portalLabel && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded ml-auto ${portalLabel.color}`}>
              {portalLabel.label}
            </span>
          )}
        </div>

        {/* Header — órgão + objeto */}
        <div className="flex items-start gap-3 mb-3">
          <ChevronRight className={`flex-shrink-0 mt-0.5 transition-transform text-slate-500 ${isExpanded ? 'rotate-90' : ''}`} size={18} />
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2 flex-wrap">
              <h3 className="text-slate-100 font-semibold text-sm truncate" title={getOrgaoDisplay(item)}>
                {getOrgaoDisplay(item) || 'Sem órgão'}
              </h3>
              <span className="text-slate-500 text-xs font-mono">
                {item.numeroCompra ? `Nº ${formatNumeroCompraComAno(item.numeroCompra, item.anoCompra)}` : ''}
                {item.codigoUnidade ? ` · UASG ${item.codigoUnidade}` : ''}
              </span>
            </div>
            <div className="text-xs text-slate-400 mt-0.5 line-clamp-1" title={item.objetoCompra || session.proposal?.title}>
              {item.objetoCompra || session.proposal?.title || ''}
            </div>
          </div>
        </div>

        {/* R1.10: 1 linha horizontal compacta com todas as info — em vez de 3-col esparso */}
        <div className="pl-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
          {/* Proposta — prazo de ENVIO (dataEncerramento) é o que importa. Rótulo
              claro "até DD/MM HH:MM", range sempre cronológico (se vier invertido,
              ordena), badge "Prorrogada" quando o encerramento foi adiado. */}
          {(item.dataAbertura || item.dataEncerramento) && (() => {
            const fmtHM = (x: any) => new Date(x).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });
            const ab = item.dataAbertura ? new Date(item.dataAbertura) : null;
            const en = item.dataEncerramento ? new Date(item.dataEncerramento) : null;
            // garante cronológico: início = o mais cedo, fim = o mais tarde
            let ini = ab, fim = en;
            if (ab && en && ab.getTime() > en.getTime()) { ini = en; fim = ab; }
            const prorrog = getProrrogacao(item);
            const showProrrog = !!prorrog && !isLicitacaoOver(item);
            return (
              <div className="flex items-center gap-1.5 text-slate-400">
                <Clock size={12} className="text-slate-500" />
                <span className="text-[10px] uppercase tracking-wide text-slate-500">Proposta</span>
                {fim
                  ? <span className="font-mono" title={ini ? `Abre ${fmtDate(ini)} ${fmtHM(ini)}` : undefined}>até {fmtDate(fim)} {fmtHM(fim)}</span>
                  : <span className="font-mono">abre {fmtDate(ini)} {fmtHM(ini)}</span>}
                {showProrrog && (
                  <span className="text-[9px] px-1 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-700/40" title={`Prorrogada: ${fmtDateTime(prorrog!.from)} → ${fmtDateTime(prorrog!.to)}`}>⏱ Prorrogada</span>
                )}
              </div>
            );
          })()}
          {(fimContagemTs || session.disputeStartTime || session.disputeEndTime) && (() => {
            // Prefere o fim REAL do portal (dataHoraFimContagem) — o disputeStartTime/
            // disputeEndTime cadastrados na criação podem estar errados (ex.: 08:00–08:00).
            const fmtT = (d: Date) => d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });
            const fim = fimContagemTs ? new Date(fimContagemTs) : (session.disputeEndTime ? new Date(session.disputeEndTime) : null);
            const ini = session.disputeStartTime ? new Date(session.disputeStartTime) : null;
            const dateRef = fim || ini;
            // Quando a sessão ainda NÃO abriu (configurando/aguardando) E o horário
            // está no futuro, o "fimContagemTs" provavelmente é o INÍCIO (Pregão:
            // dataEncerramento do PNCP = início da sessão pública). "até HH:MM"
            // seria semanticamente errado — mostra "abre HH:MM" pra deixar claro.
            const naoAbriu = ['configurando', 'aguardando'].includes(session.status);
            const naoTemFimVivo = !fimContagemTs || (item?.dataSessaoFim == null
              && (!Array.isArray(session?.rankingData?.itensGrupo?.['-1']) || session.rankingData.itensGrupo['-1'].length === 0));
            // Pregão Aberto/Fechado NÃO tem fim previsível (HAR 27/05/26: /participacao
            // só expõe dataHoraAbertura, sem dataHoraFimDisputa). Sem fimContagem vivo,
            // o `fim` cacheado é o início. Tratar como "abre HH:MM" em qualquer status.
            const isPregaoAbertoFechado = tipo.isAbertoFechado;
            // "abre HH:MM" só pra PREGÃO (no PNCP a dataEncerramento = INÍCIO da
            // sessão pública). Pra DISPENSA o fim/disputeEndTime é o ENCERRAMENTO
            // real (janela fixa: abre X, encerra Y) → sempre "até". (fix 02/06/26:
            // mostrava "abre 16:29" numa Dispensa que ABRIU 10:29 e encerra 16:29.)
            const isDispensa = /dispens/i.test(item?.modalidadeNome || '');
            const horarioEhInicio = (naoAbriu || isPregaoAbertoFechado) && naoTemFimVivo && fim && fim.getTime() > Date.now() && !isDispensa;
            // Janela clara início–fim (ex.: Dispensa abre 08:00 encerra 14:00):
            // quando há início E fim no MESMO dia e não é caso "abre", mostra o
            // range completo em vez de só "até HH:MM".
            const fimReal = fimContagemTs ? new Date(fimContagemTs) : fim;
            const mesmoDia = ini && fimReal
              && ini.toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' }) === fimReal.toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
            const temRange = !horarioEhInicio && ini && fimReal && ini.getTime() < fimReal.getTime() && mesmoDia;
            return (
              <div className="flex items-center gap-1.5 text-slate-400">
                <span className="text-[10px] uppercase tracking-wide text-slate-500">Sessão</span>
                {isToday ? <span className="text-amber-300 font-bold">HOJE</span>
                  : dateRef && <span className="font-mono">{fmtDate(dateRef)}</span>}
                <span className="font-mono text-slate-500">
                  {horarioEhInicio && fim
                    ? ` abre ${fmtT(fim)}`
                    : temRange && ini && fimReal
                      ? ` ${fmtT(ini)}–${fmtT(fimReal)}`
                      : fimContagemTs
                        ? ` até ${fmtT(new Date(fimContagemTs))}`
                        : fim
                          ? ` até ${fmtT(fim)}`
                          : `${ini ? ' ' + fmtT(ini) : ''}`}
                </span>
              </div>
            );
          })()}
          {tempoRestanteMs !== null && (
            <div className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-amber-500/15 border border-amber-700/40">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
              <span className="text-amber-300 font-mono font-bold">{fmtCountdown(tempoRestanteMs)}</span>
              <span className="text-[9px] text-amber-400/80 uppercase">restante</span>
            </div>
          )}

          {/* F-6 (Onda 2): idade do dado no card COLAPSADO. O countdown é timer
              client-side — com o backend/robô mudo a tela seguia "viva" com dado
              morto; o badge de staleness só existia expandido. >30s âmbar, >2min
              vermelho (o robô ativo atualiza a cada poucos segundos). */}
          {session.status === 'ativo' && session.lastUpdate && (() => {
            const ageS = Math.floor((Date.now() - new Date(session.lastUpdate).getTime()) / 1000);
            if (ageS < 30) return null;
            return (
              <span
                className={`text-[10px] px-1.5 py-0.5 rounded border font-mono ${
                  ageS > 120 ? 'bg-red-900/30 border-red-700 text-red-300 animate-pulse' : 'bg-amber-900/30 border-amber-700/60 text-amber-300'
                }`}
                title="Tempo desde o último update recebido do robô/portal — o countdown continua andando mesmo sem dado novo"
              >
                ⏱ dados há {ageS >= 60 ? `${Math.floor(ageS / 60)}min` : `${ageS}s`}
              </span>
            );
          })()}

          {/* A última decisão do robô (backend #471): sem ela, "segurando de propósito" e "robô morto" eram iguais na
              tela (sessão 163, 16/10/2026). Só com a disputa no ar: depois dela, "segurando" seria passado dito no
              presente. O `at` é o ÚLTIMO registro (o backend regrava a cada 60 s com o mesmo motivo), daí "às". */}
          {['ativo', 'aguardando'].includes(session.status) && (() => {
            const d = decisaoDoRobo(session);
            if (!d) return null;
            return (
              // Só o motivo encurta: o rótulo e a hora ficam sempre à vista (cortar a hora esconde se o robô segue vivo).
              <span
                data-ultima-decisao={d.tom}
                className={`inline-flex items-center gap-1 min-w-0 max-w-[32rem] text-[10px] px-1.5 py-0.5 rounded ${d.tom === 'segurando' ? TONE_BADGE.warning : TONE_BADGE.success}`}
                title={`${d.rotulo}: ${d.motivo} — último registro às ${d.hora}`}
              >
                <span className="shrink-0 font-semibold">{d.rotulo}:</span>{' '}
                <span data-motivo className="truncate">{d.motivo}</span>{' '}
                <span data-hora className="shrink-0 font-mono">· às {d.hora}</span>
              </span>
            );
          })()}

          {/* Separador visual */}
          {fornecedoresCount > 0 && <span className="text-slate-700">·</span>}

          {/* Resultado */}
          {fornecedoresCount > 0 && (
            <div className="flex items-center gap-3">
              <span className="text-slate-400">
                <span className="text-slate-200 font-semibold">{fornecedoresCount}</span> fornec.
              </span>
              {minhaClassif != null && (
                <span
                  className={`font-bold ${minhaClassif === 1 ? 'text-emerald-400' : minhaClassif <= 3 ? 'text-amber-400' : 'text-slate-300'}`}
                  title={classifDerivada ? 'Posição derivada do valor (não convocada para a fase fechada)' : undefined}
                >
                  IPCOM {minhaClassif}º{classifDerivada ? '*' : ''}
                </span>
              )}
              {session.proposal?.totalValue && (
                <span className="text-slate-300 font-mono">{fmtCurrency(session.proposal.totalValue)}</span>
              )}
            </div>
          )}

          {/* Status badges */}
          <div className="flex items-center gap-1">
            {/* Onda 2 (F-3): modo do robô SEMPRE visível no card — antes só
                dentro do modal de Configurações, e o operador não tinha como
                confirmar "está REAL?" durante a disputa. Dado já vem no
                bidConfig; piso Σ soma os limitValue configurados. */}
            {!['finalizado', 'erro'].includes(session.status) && secao !== 'finalizadas' && session.bidConfig && (() => {
              const dry = session.bidConfig?.dryRun !== false;
              const pisoTotal = (session.itemLimits || []).reduce((acc: number, il: any) => {
                const v = Number(il?.limitValue) || 0;
                const q = Number(il?.quantity) || 1;
                return acc + v * q;
              }, 0);
              const topN = Number(session.strategies?.coverTopN) || 0;
              // D1 (L7): com o estado único, a configuração vira UM bloco cinza ("robô real · servidor · piso"), com o
              // modo em negrito, porque REAL x SIMULAÇÃO é o que o operador confere antes de iniciar.
              if (estado) {
                const partes = [session.bidConfig?.engine === 'servidor' ? 'servidor' : null, pisoTotal > 0 ? `piso Σ ${fmtCurrency(pisoTotal)}${topN > 0 ? ` · top-${topN}` : ''}` : null].filter(Boolean);
                return (
                  <span className="text-[10px] px-2 py-0.5 rounded bg-central-elevado border border-central-borda text-slate-400" data-configuracao-do-robo
                    title={dry ? 'Robô em SIMULAÇÃO: só loga "lançaria X", não envia' : 'Robô em modo REAL: envia lances de verdade no portal'}>
                    robô <b className="text-white">{dry ? 'simulação' : 'real'}</b>{partes.length ? ` · ${partes.join(' · ')}` : ''}
                  </span>
                );
              }
              return (
                <>
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                      dry ? 'bg-amber-500/20 text-amber-300 border border-amber-700/50' : 'bg-red-500/20 text-red-300 border border-red-700/60'
                    }`}
                    title={dry ? 'Robô em SIMULAÇÃO: só loga "lançaria X", não envia' : 'Robô em modo REAL: envia lances de verdade no portal'}
                  >
                    {dry ? '🧪 SIMULAÇÃO' : '🤖 REAL'}
                  </span>
                  {/* Motor servidor (03/09/2026): sem aba do Firefox, roda em paralelo
                      com outra disputa. O operador precisa saber qual motor está no
                      comando antes de olhar o log. */}
                  {session.bidConfig?.engine === 'servidor' && (
                    <span
                      className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-700/50"
                      title="Motor servidor: poll do em-disputa, decisão e lance no backend, sem aba do Firefox"
                    >
                      🖥️ servidor
                    </span>
                  )}
                  {pisoTotal > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700/60 text-slate-300" title={`Piso (soma dos limites por item × qtd) · posição-alvo ${topN === 0 ? 'auto' : `top-${topN}`}`}>
                      piso Σ {fmtCurrency(pisoTotal)}{topN > 0 ? ` · top-${topN}` : ''}
                    </span>
                  )}
                </>
              );
            })()}
            {/* RANKING AO VIVO INDISPONÍVEL (12/08/2026). O robô só enxerga o
                pelotão dos concorrentes pela aba "Melhores valores por
                fornecedor", que a extensão intercepta. Quando esse feed para,
                ele SEGURA a margem em vez de decidir pela foto da abertura — e
                isso precisa ficar na cara do operador. No FLORAI (PE 1/2026) a
                sessão ficou 11 minutos parada com dois concorrentes na faixa
                útil e a única pista na tela era um "Ranking de propostas não
                coletado ainda" apagado, que parecia "ainda vai chegar". */}
            {session.status === 'ativo' && session.rankingLiveStatus?.aoVivo === false && (() => {
              const rl: any = session.rankingLiveStatus;
              // O ESTADO, quando o backend manda (15/10/2026): uma faixa por estado, na linha inteira, em vez do selo que
              // juntava "não lê" e "tabela atrasada". Ver utils/estadoDoRanking. Sem `estado` (backend antigo), o de antes.
              const faixa = faixaDoRanking(rl, now);
              if (faixa) {
                const tom = faixa.estado === 'motor-sem-leitura' ? 'danger' : 'warning';
                return (
                  <div
                    role="status"
                    data-estado-do-ranking={faixa.estado}
                    title={rl.motivo || undefined}
                    className={`basis-full w-full mt-1 px-3 py-2 rounded-lg border text-xs ${TONE_SURFACE[tom]} ${TONE_BORDER[tom]}`}
                  >
                    <p className={`font-semibold flex items-center gap-1.5 ${TONE_TEXT[tom]}`}><AlertTriangle className="w-3.5 h-3.5 shrink-0" />{faixa.titulo}</p>
                    <p className="text-slate-300 mt-0.5">{faixa.texto}</p>
                  </div>
                );
              }
              const desdeMin = rl.desde
                ? Math.max(0, Math.round((Date.now() - new Date(rl.desde).getTime()) / 60000))
                : null;

              // ⛔ "fonte=nenhuma" com motor SERVIDOR tem causa e conserto
              // conhecidos, e até 24/09/2026 a tela não dizia nenhum dos dois.
              // O `em-disputa` que o motor servidor lê traz LÍDER e NOSSO VALOR e
              // mais nada — o pelotão do meio só existe na tabela MVPF do DOM, que
              // só a extensão enxerga. O backend enfileira o sensor sozinho no
              // start; se o Firefox não estiver de pé e logado, o job nunca é
              // consumido e a `fonte` fica `nenhuma` para sempre.
              //
              // TRT-8 PE 90009/2026: 17 minutos de disputa, oito bloqueios, e o
              // 4º lugar estava a R$ 3.121,20 DENTRO do piso. O operador só
              // descobriu a causa lendo o log do servidor. O aviso antigo dizia
              // "assuma na mão", como se não houvesse conserto — e havia.
              const motorServidor = String((session as any)?.bidConfig?.engine || '') === 'servidor';
              const sensorCego = !rl.fonte || rl.fonte === 'nenhuma';
              const idade = desdeMin != null ? ` (${desdeMin}min)` : '';

              if (motorServidor && sensorCego) {
                return (
                  <span
                    className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-200 border border-red-600/60 font-semibold"
                    title={'O motor SERVIDOR enxerga só o líder e você — o pelotão do meio vem da tabela MVPF, que só a extensão lê.\n\nO sensor é enfileirado sozinho ao Iniciar; se a `fonte` está em `nenhuma`, o job não foi consumido: confira se o Firefox com a extensão está aberto e logado no ComprasNet.\n\nEnquanto isso o robô segura a margem — ele não desce às cegas.'}
                  >
                    ⚠ Sensor do pelotão fora do ar — robô só vê o líder{idade}
                  </span>
                );
              }

              return (
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-200 border border-amber-600/60 font-semibold"
                  title={`${rl.motivo || 'Sem ranking ao vivo dos concorrentes.'}\n\nO robô não desce sem enxergar o pelotão — assuma na mão se quiser disputar posição.`}
                >
                  ⚠ Ranking ao vivo indisponível — robô segurando{idade}
                </span>
              );
            })()}
            {/* ⛔ GRUPO DESCONHECIDO — o aviso que teria evitado o PE 22/2026
                COREN-PE (20/08): 9 itens sem grupo, o robô avaliou item a item,
                o piso de cada um bloqueou o lance que o TOTAL comportava e ele
                não lançou nada numa disputa ganhável (piso somado R$ 133.900
                contra líder em R$ 139.360).

                Fica no CARD, não só no modal, porque quem opera não abre as
                configurações antes de cada disputa — e depois que a sessão abre
                já é tarde pra sincronizar. */}
            {(() => {
              const lim: any[] = Array.isArray(session.itemLimits) ? session.itemLimits : [];
              const semGrupo = lim.length > 1 && lim.every((il: any) => il.groupNumber == null && il.groupIdentificador == null);
              if (!semGrupo || ['finalizado', 'encerrada'].includes(String(session.status)) || secao === 'finalizadas') return null;
              return (
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-200 border border-amber-600/60 font-semibold"
                  title={'Os itens estão sem informação de grupo. Se o edital julgar POR GRUPO (pelo total), o robô disputa item a item e o piso de cada item impede o lance que o total comportaria — ele não lança.\n\nO PNCP não informa agrupamento: use "Sincronizar do Portal" antes de iniciar.'}
                >
                  ⚠ {lim.length} itens sem grupo — sincronize antes de iniciar
                </span>
              );
            })()}
            {estado ? (
              <span data-estado-do-cartao className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${TONE_BADGE[estado.tom]} ${estado.tom === 'live' ? 'animate-pulse' : ''}`}>
                {estado.rotulo}
              </span>
            ) : (
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${badge.color} ${session.status === 'ativo' ? 'animate-pulse' : ''}`}>
              {badge.label}
            </span>
            )}
            {/* Coleta do resultado (poll agendado da seleção) */}
            {!estado && session.resultData?.selecaoPoll && (() => {
              const sp = session.resultData.selecaoPoll;
              const t = sp.lastCheckAt
                ? new Date(sp.lastCheckAt).toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit' })
                : '';
              if (sp.status === 'capturado') {
                return (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 font-medium" title={`Ranking final coletado às ${t}`}>
                    📊 Resultado coletado
                  </span>
                );
              }
              if (sp.status === 'aguardando_julgamento') {
                return (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300" title={`Aguardando o pregoeiro encerrar/julgar. Última checagem ${t} · ${sp.attempts || 0} tentativa(s). O sistema coleta sozinho a cada 30 min.`}>
                    ⏳ Aguardando julgamento {t && `(${t})`}
                  </span>
                );
              }
              if (sp.status === 'erro') {
                return (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300" title={`Erro na última coleta (${t}). Vai tentar de novo no próximo ciclo.`}>
                    ⚠ Erro na coleta
                  </span>
                );
              }
              return null;
            })()}
            {!estado && sessionOutcome.compraEncerrada && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 font-medium" title="Todos os grupos homologados">
                Compra encerrada
              </span>
            )}
            {!estado && sessionOutcome.ipcom && phaseInfo.convocado !== false && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${sessionOutcome.ipcom.color} font-medium`} title={sessionOutcome.ipcom.title}>
                {sessionOutcome.ipcom.label}
              </span>
            )}
            {/* `finalizado` sem desfecho: a disputa acabou mas o pregoeiro ainda
                não abriu a Seleção de Fornecedores, então o ranking final não
                existe no portal pra ser coletado. Sem este selo a sessão fica
                idêntica a uma encerrada COM resultado e ninguém volta pra
                coletar. (28/08/2026, Dispensa 18/2026 MAPA) */}
            {!estado && sessionOutcome.resultadoPendente && (
              <span
                className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 font-medium"
                title="A disputa encerrou, mas o resultado ainda não foi coletado — o pregoeiro não abriu a Seleção de Fornecedores no portal. A coleta automática re-tenta sozinha; o botão de coletar também funciona assim que a Seleção abrir."
              >
                Resultado pendente
              </span>
            )}
            {/* ⛔ "Não convocado" mentia. `convocado` é uma flag VIVA que o
                encerramento zera (fase 'E' → false), então TODA sessão de AF
                terminava exibindo "não convocado" — inclusive a Pederneiras
                15/2026 (14/09/2026), onde a IPCOM foi convocada e mandou o
                lance fechado de R$ 4.200.

                O fato histórico agora tem marca própria: `melhorAberturaAF` é
                gravado UMA vez, no instante em que a convocação acende, e nunca
                reescrito. Presente = fomos convocados. Ausente numa sessão
                antiga = não se sabe — e aí o selo se cala, que é melhor que
                afirmar o contrário. */}
            {(() => {
              const lims: any[] = Array.isArray(session.itemLimits) ? session.itemLimits : [];
              const terminou = phaseInfo.phase === 'fechada' || phaseInfo.phase === 'encerrada';
              if (!terminou || estado) return null;
              const foiConvocado = lims.some((l: any) => l?.melhorAberturaAF != null);
              if (foiConvocado) {
                return (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300" title="A IPCOM entrou no corte de 10% e foi convocada para o lance fechado">
                    Convocado
                  </span>
                );
              }
              // Só afirma o negativo quando a sessão é nova o bastante pra ter o
              // marcador — do contrário ficaria dizendo "não convocado" pra toda
              // AF anterior à mudança.
              const temMarcadorDisponivel = lims.some((l: any) => l?.melhorItem != null);
              if (!temMarcadorDisponivel || phaseInfo.convocado !== false) return null;
              return (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300" title="A IPCOM ficou fora do corte de 10% — sem direito ao lance fechado">
                  Não convocado
                </span>
              );
            })()}
          </div>

          {/* Quadro Informativo — só aparece se tem algo */}
          {hasQi && (
            <>
              <span className="text-slate-700">·</span>
              <div className="flex items-center gap-1">
                <MessageCircle size={12} className="text-slate-500" />
                {qtdeEscla > 0 && (
                  <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300 border border-blue-700/40" title="Esclarecimentos">
                    {qtdeEscla} esclarec.
                  </span>
                )}
                {qtdeAvisos > 0 && (
                  <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-700/40" title="Avisos">
                    <AlertTriangle size={9} />
                    {qtdeAvisos} aviso{qtdeAvisos !== 1 ? 's' : ''}
                  </span>
                )}
                {qtdeImpug > 0 && (
                  <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-300 border border-red-700/40" title="Impugnações">
                    <AlertTriangle size={9} />
                    {qtdeImpug} impug.
                  </span>
                )}
              </div>
            </>
          )}

          {/* Responsável (se disponível) */}
          {responsavelNome && (
            <>
              <span className="text-slate-700">·</span>
              <div className="flex items-center gap-1 text-slate-400">
                <Phone size={11} />
                <span className="text-[10px] truncate max-w-[200px]" title={responsavelNome}>{responsavelNome}</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ============ Ações (sempre visível, abaixo do head) ============ */}
      <div className="px-4 pb-3 flex items-center justify-end gap-1 border-t border-central-borda pt-2" onClick={(e) => e.stopPropagation()}>
        {/* Abrir no portal — o Cadastrar e o Monitorar já tinham; a tela onde
            mais se precisa dele (a disputa acontecendo) não tinha, e o operador
            saía procurando a compra no portal na mão, no minuto da sessão. */}
        {(() => {
          const link = portalLinkUrl(item);
          if (!link) return null;
          return (
            <a
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              title={link.title}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-slate-400 hover:text-white hover:bg-central-elevado rounded transition-colors mr-auto"
            >
              <ExternalLink size={14} /> Portal
            </a>
          );
        })()}
        {/* Iniciar/Reiniciar: só enquanto a janela do portal está de pé. Sessão
            encerrada não tem o que iniciar — o clique acordaria o robô à toa e
            consumiria a sessão do portal (login único). */}
        {!janelaFechada && ['configurando', 'pausado', 'erro'].includes(session.status) && (
          <button onClick={onStart} title={session.status === 'erro' ? 'Reiniciar' : 'Iniciar disputa'}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-emerald-400 hover:bg-emerald-500/10 rounded transition-colors">
            <Play size={14} /> {session.status === 'erro' ? 'Reiniciar' : 'Iniciar'}
          </button>
        )}
        {/* Coletar resultado: em TODOS os estados. Em 'configurando' só depois que a
            janela da disputa passou — antes disso não há nada no portal pra coletar.
            Serve pra coletar o resultado final E pra verificar avanço de etapas/ranking
            a qualquer momento (sugestão user 03/06).
            03/09/2026 (Tremembé DE 24/2026, sessão 125): a sessão nunca rodou (a disputa
            foi no braço, porque a trava de 1 ComprasNet por vez estava ocupada pelo
            Fuzileiros) e o card só tinha "Iniciar" — sem caminho na tela pra puxar o
            resultado. O backend já aceitava a coleta em 'configurando' e até promove
            pra 'finalizado' quando a janela expirou; faltava o botão. */}
        {onCollect && (session.status !== 'configurando' || encerrada) && (
          <button onClick={onCollect} disabled={collecting} title="Coletar resultado final do portal"
            className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors disabled:opacity-60 ${
              situacao.lancesEncerrados
                ? 'text-sky-200 bg-sky-500/15 border border-sky-600/50 hover:bg-sky-500/25 font-medium'
                : 'text-sky-400 hover:bg-sky-500/10'
            }`}>
            <FileText size={14} className={collecting ? 'animate-spin' : ''} /> {collecting ? 'Coletando...' : 'Coletar resultado'}
          </button>
        )}
        {/* status='finalizado' não tem ação de Iniciar/Reiniciar — fim do ciclo.
            Recoleta do ranking (quando faltar) fica na aba Itens via botão dedicado.
            ⛔ Com os lances encerrados não há o que parar: a sessão segue `ativo`
            porque o robô ainda espera o pregoeiro abrir a Seleção, e "Parar" ali
            oferecia interromper algo que já acabou, sem dizer o que interrompia
            (sessão 150, 01/10/2026). A única ação que faz sentido é coletar. */}
        {['ativo', 'aguardando'].includes(session.status) && !situacao.lancesEncerrados && (
          <button onClick={onStop} title="Parar"
            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-red-400 hover:bg-red-500/10 rounded transition-colors">
            <Pause size={14} /> Parar
          </button>
        )}
        <button onClick={onConfig} title="Configurações"
          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-slate-400 hover:bg-central-elevado rounded transition-colors">
          <Settings size={14} />
        </button>
        <button onClick={onDelete} title="Remover"
          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors">
          <Trash2 size={14} />
        </button>
      </div>

      {/* ============ Expanded content ============ */}
      {isExpanded && expandedContent && (
        <div className="border-t border-central-borda bg-central-elevado">
          {expandedContent}
        </div>
      )}
    </div>
  );
}
